import type { BotBusinessHours, BusinessHoursWindow, HandoffMode } from '../../types/leadBot.js';

// SMS Lead Bot — business hours, and the two handoff modes they decide. PURE.
//
// ⚠️ THE BOT CANNOT HAND OFF TO A SLEEPING SALESPERSON. In business hours a
// handoff means "notify the salesperson now". Out of hours it means "capture
// and queue" — "I'll have someone call you first thing, what time suits?" —
// which is a different reply, not just a different notification. That is why
// the distinction lives in the design from Phase 0 even though the notification
// itself is Phase 2: the WORDS the bot uses depend on it.
//
// ⚠️ THE TIMEZONE IS THE CLIENT'S, NOT OURS. A Perth dealership's "first thing"
// is not a Brisbane afternoon, and the agency runs across AU and NZ. Resolution
// goes through Intl with an explicit IANA zone rather than the server's local
// time, which on Cloud Run is UTC and would put every evening handoff in the
// wrong bucket.

export const DEFAULT_BUSINESS_TIMEZONE = 'Australia/Brisbane';

/** Mon–Fri 8:30–17:00, Sat 9:00–15:00. A starting point an AM edits, not a rule. */
export const DEFAULT_BUSINESS_HOURS: BotBusinessHours = {
  timezone: DEFAULT_BUSINESS_TIMEZONE,
  windows: [
    { day: 1, openTime: '08:30', closeTime: '17:00' },
    { day: 2, openTime: '08:30', closeTime: '17:00' },
    { day: 3, openTime: '08:30', closeTime: '17:00' },
    { day: 4, openTime: '08:30', closeTime: '17:00' },
    { day: 5, openTime: '08:30', closeTime: '17:00' },
    { day: 6, openTime: '09:00', closeTime: '15:00' },
  ],
};

export const DAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const DAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

/** "HH:MM" → minutes since local midnight, or null when unparseable. */
export function parseTimeOfDay(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec((value ?? '').trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
}

export interface LocalClock {
  /** 0=Sunday … 6=Saturday, in the target zone. */
  day: number;
  /** Minutes since local midnight, in the target zone. */
  minutes: number;
}

/**
 * The instant `now` expressed in `timezone`. Falls back to the machine's own
 * clock when the zone string is not one Intl recognises — a bad zone should
 * degrade to a plausible answer, not throw inside a send path.
 */
export function localClock(now: Date, timezone: string): LocalClock {
  try {
    const parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: timezone,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(now);
    const lookup = (type: string): string =>
      parts.find((p) => p.type === type)?.value ?? '';
    const day = DAY_INDEX[lookup('weekday')];
    const hour = Number(lookup('hour'));
    const minute = Number(lookup('minute'));
    if (day === undefined || Number.isNaN(hour) || Number.isNaN(minute)) {
      throw new Error('unreadable parts');
    }
    // Intl renders midnight as "24" in some locales/engines under hour12:false.
    return { day, minutes: (hour % 24) * 60 + minute };
  } catch {
    return { day: now.getDay(), minutes: now.getHours() * 60 + now.getMinutes() };
  }
}

export function isWithinBusinessHours(now: Date, hours: BotBusinessHours): boolean {
  const windows = hours?.windows ?? [];
  if (windows.length === 0) return false;
  const clock = localClock(now, hours.timezone || DEFAULT_BUSINESS_TIMEZONE);
  return windows.some((window: BusinessHoursWindow) => {
    if (window.day !== clock.day) return false;
    const open = parseTimeOfDay(window.openTime);
    const close = parseTimeOfDay(window.closeTime);
    // ⚠️ An unparseable window is treated as CLOSED, not as open-all-day. Out of
    // hours the bot queues a callback, which is a safe thing to say at any time
    // of day; "we'll call you right now" at 2am is not.
    if (open === null || close === null || close <= open) return false;
    return clock.minutes >= open && clock.minutes < close;
  });
}

/**
 * Which kind of handoff is possible right now. `notify` = someone is there.
 * `queue` = capture the lead and promise a callback.
 */
export function resolveHandoffMode(now: Date, hours: BotBusinessHours): HandoffMode {
  return isWithinBusinessHours(now, hours) ? 'notify' : 'queue';
}

/** One line for the prompt, so the model's wording matches the reality. */
export function describeAvailability(mode: HandoffMode): string {
  return mode === 'notify'
    ? 'The sales team is available right now — say someone will be in touch shortly.'
    : 'The sales team is NOT available right now (outside business hours) — do not ' +
        'promise an immediate call. Offer a callback at the next opening and ask what ' +
        'time suits them.';
}
